import { Service } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { ProfileStateModel } from './profile-state.model';
import {
  ProfileStateClear,
  ProfileStateSetAppDeviceId,
  ProfileStateSetBrowserLanguage,
  ProfileStateSetIsLoggedIn,
  ProfileStateSetKundeDokumentenManagerAktiv,
  ProfileStateSetProfile,
  ProfileStateUpdatePasswordExpiration,
} from './profile.actions';
import { LexplusPermissionsEnum, LexplusProfileDto } from '@lexplus-authentication-api';

const DEFAULT_STATE: ProfileStateModel = {
  profile: undefined,
  appDeviceId: undefined,
  isLoggedIn: false,
  browserLanguage: 'de',
};

@State<ProfileStateModel>({
  name: 'state__profile',
  defaults: DEFAULT_STATE,
})
@Service({ autoProvided: false })
export class ProfileState {
  @Selector()
  public static profileLanguage(state: ProfileStateModel): string | undefined {
    if (state) {
      return state.profile?.sprache;
    }
    return undefined;
  }

  @Selector()
  public static language(state: ProfileStateModel): string {
    return state.profile?.sprache ?? state.browserLanguage ?? 'de';
  }

  @Selector()
  public static isAdmin(state: ProfileStateModel): boolean {
    return state.profile?.istNeosysAdmin ?? false;
  }

  @Selector()
  public static isKundeDokumentenManagerAktiv(state: ProfileStateModel): boolean {
    return state.profile?.istKundeDokumentenManagerAktiv ?? false;
  }

  @Selector()
  public static isKundeAdmin(state: ProfileStateModel): boolean {
    return state.profile?.istKundeAdmin ?? false;
  }

  /** Whether the user subscribed to the newsletter — what opens Downloads → Newsletter without any other right. */
  @Selector()
  public static isNewsletterSubscriber(state: ProfileStateModel): boolean {
    return state.profile?.newsletterAktiv ?? false;
  }

  /**
   * Whether the user is a newsletter subscriber and nothing else: no admin flag and no permission.
   * Such a user's only page is their newsletter archive.
   */
  @Selector()
  public static isNewsletterOnly(state: ProfileStateModel): boolean {
    const profile = state.profile;
    return !!profile?.newsletterAktiv && !profile.istNeosysAdmin && !profile.istKundeAdmin && !(profile.permissions ?? []).length;
  }

  @Selector()
  public static isLoggedIn(state: ProfileStateModel): boolean {
    return state && state.isLoggedIn;
  }

  @Selector()
  public static appDeviceId(state: ProfileStateModel): string | undefined {
    return state.appDeviceId;
  }

  @Selector()
  public static profile(state: ProfileStateModel): LexplusProfileDto | undefined {
    return state.profile;
  }

  @Selector()
  public static passwordExpiration(state: ProfileStateModel):
    | {
        showBanner: boolean;
        passwordExpirationDate: string;
      }
    | undefined {
    return state.passwordExpiration;
  }

  /**
   * Parameterized selector: checks whether the current user holds a permission (or
   * one of several).
   *
   * Admins (Neosys or Kunde admin) have full access — independent of the permission
   * matrix; this mirrors `hasFullAccess` in the backend. With
   * `ignoreGlobalAdmin = true` the admin override is bypassed and only the concrete
   * permission is checked, for the cases where that is explicitly wanted.
   *
   * `UserDokumentenmanager` is withheld from everybody, admins included, while
   * `istKundeDokumentenManagerAktiv` is off.
   */
  public static hasPermission(permission: LexplusPermissionsEnum | LexplusPermissionsEnum[], ignoreGlobalAdmin = false) {
    return createSelector([ProfileState], (state: ProfileStateModel): boolean => {
      const profile = state.profile;
      if (!profile) {
        return false;
      }

      const requested = Array.isArray(permission) ? permission : [permission];
      const grantable = requested.filter(
        (entry) => entry !== LexplusPermissionsEnum.UserDokumentenmanager || (profile.istKundeDokumentenManagerAktiv ?? false),
      );
      if (grantable.length === 0) {
        return false;
      }

      if (!ignoreGlobalAdmin && (profile.istNeosysAdmin || profile.istKundeAdmin)) {
        return true;
      }

      const granted = profile.permissions ?? [];
      return grantable.some((entry) => granted.includes(entry));
    });
  }

  @Action(ProfileStateSetProfile)
  public ProfileStateSetProfile(ctx: StateContext<ProfileStateModel>, action: ProfileStateSetProfile) {
    ctx.patchState({ profile: action.profile });
  }

  @Action(ProfileStateSetKundeDokumentenManagerAktiv)
  public ProfileStateSetKundeDokumentenManagerAktiv(
    ctx: StateContext<ProfileStateModel>,
    action: ProfileStateSetKundeDokumentenManagerAktiv,
  ) {
    const profile = ctx.getState().profile;
    if (!profile) {
      return;
    }
    ctx.patchState({ profile: { ...profile, istKundeDokumentenManagerAktiv: action.istKundeDokumentenManagerAktiv } });
  }

  @Action(ProfileStateSetAppDeviceId)
  public ProfileStateSetAppDeviceId(ctx: StateContext<ProfileStateModel>, action: ProfileStateSetAppDeviceId) {
    ctx.patchState({ appDeviceId: action.appDeviceId });
  }

  @Action(ProfileStateSetIsLoggedIn)
  public ProfileStateSetLoggedIn(ctx: StateContext<ProfileStateModel>, action: ProfileStateSetIsLoggedIn) {
    ctx.patchState({ isLoggedIn: action.isLoggedIn });
  }

  @Action(ProfileStateSetBrowserLanguage)
  public ProfileStateSetBrowserLanguage(ctx: StateContext<ProfileStateModel>, action: ProfileStateSetBrowserLanguage) {
    ctx.patchState({ browserLanguage: action.browserLanguage });
  }

  @Action(ProfileStateUpdatePasswordExpiration)
  public ProfileStateUpdatePasswordExpiration(ctx: StateContext<ProfileStateModel>, action: ProfileStateUpdatePasswordExpiration) {
    ctx.patchState({
      passwordExpiration: action.showBanner
        ? {
            showBanner: action.showBanner,
            passwordExpirationDate: <string>action.passwordExpirationDate,
          }
        : undefined,
    });
  }

  @Action(ProfileStateClear)
  public ProfileStateClear(ctx: StateContext<ProfileStateModel>) {
    // Reset everything except the appDeviceId (device trust survives logout).
    ctx.setState({ ...DEFAULT_STATE, appDeviceId: ctx.getState().appDeviceId });
  }
}
